import { ButtonCoreSize } from 'omakase/Button/ButtonCore';
import { PlainButtonVariant } from './PlainButton';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';

// TODO: Consider a single file for all Omakase style-related variables that are shared across multiple components
const focusShadowSize = '2px';

export const plainButtonSize = ({ typography }: Theme, plainButtonSize: ButtonCoreSize) => {
  switch (plainButtonSize) {
    case 'xlarge':
      return css`
        ${typography.variant('body01Strong')};
      `;
    case 'large':
    case 'medium':
    case 'small':
      return css`
        ${typography.variant('body02Strong')};
      `;
    case 'xsmall':
      return css`
        ${typography.variant('captionStrong')};
      `;
    default:
      assertNever(plainButtonSize);
  }
};

type ButtonInteractionType = 'enabled' | 'hover' | 'focus' | 'active' | 'disabled';

const plainButtonThemeInteractionBasic = (
  { colors }: Theme,
  plainButtonInteractionType: ButtonInteractionType
) => {
  switch (plainButtonInteractionType) {
    case 'enabled':
      return css`
        color: ${colors.fgDefault};
      `;
    case 'focus':
      return '';
    case 'hover':
      return css`
        color: ${colors.fgDefault};
        text-decoration-line: underline;
      `;
    case 'active':
      return css`
        color: ${colors.fgDefault};
        background-color: ${colors.bgLight};
        text-decoration-line: underline;
      `;
    case 'disabled':
      return css`
        color: ${colors.fgDisabled};
      `;
    default:
      assertNever(plainButtonInteractionType);
  }
};

const plainButtonThemeInteractionPrimary = (
  { colors }: Theme,
  plainButtonInteractionType: ButtonInteractionType
) => {
  switch (plainButtonInteractionType) {
    case 'enabled':
      return css`
        color: ${colors.fgPrimaryBase};
      `;
    case 'focus':
      return '';
    case 'hover':
      return css`
        color: ${colors.bgPrimaryDark};
        text-decoration-line: underline;
      `;
    case 'active':
      return css`
        color: ${colors.fgPrimaryDark};
        background-color: ${colors.bgLight};
        text-decoration-line: underline;
      `;
    case 'disabled':
      return css`
        color: ${colors.fgDisabled};
      `;
    default:
      assertNever(plainButtonInteractionType);
  }
};

const plainButtonThemeFromVariantAndInteractionTypes = (
  theme: Theme,
  plainButtonVariant: PlainButtonVariant,
  plainButtonInteractionType: ButtonInteractionType
) => {
  switch (plainButtonVariant) {
    case 'basic':
      return plainButtonThemeInteractionBasic(theme, plainButtonInteractionType);
    case 'primary':
      return plainButtonThemeInteractionPrimary(theme, plainButtonInteractionType);
    default:
      assertNever(plainButtonVariant);
  }
};

export const plainButtonVariant = (theme: Theme, plainButtonVariant: PlainButtonVariant) => [
  plainButtonThemeFromVariantAndInteractionTypes(theme, plainButtonVariant, 'enabled'),
  theme.interactiveStates.hover(css`
    ${plainButtonThemeFromVariantAndInteractionTypes(theme, plainButtonVariant, 'hover')}
  `),
  theme.interactiveStates.focus(css`
    ${plainButtonThemeFromVariantAndInteractionTypes(theme, plainButtonVariant, 'focus')};
    z-index: ${theme.zIndex.bump};
    box-shadow: 0 0 0 ${focusShadowSize} ${theme.colors.borderPrimaryBase};
  `),
  theme.interactiveStates.active(css`
    ${plainButtonThemeFromVariantAndInteractionTypes(theme, plainButtonVariant, 'active')}
  `),
  theme.interactiveStates.disabled(css`
    ${plainButtonThemeFromVariantAndInteractionTypes(theme, plainButtonVariant, 'disabled')}
  `),
];

export const plainButtonBase = ({ timings, borderRadius }: Theme) => css`
  border-radius: ${borderRadius[100]};
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  outline: none;
  overflow: hidden;
  padding: 2px 0;
  position: relative;
  text-align: center;
  text-overflow: ellipsis;
  transition: all ${timings.duration.shortest} ${timings.timingFunction};
  white-space: nowrap;
  z-index: initial;

  a& {
    text-decoration: none;
  }

  button& {
    appearance: none;
    &:not(:disabled) {
      cursor: pointer;
    }
  }
`;
